/** @jsx isolateComponent */

import { css } from '@emotion/core';

import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { Colors, Link, Shadows, Typography } from '@zapier/design-system';

import type { UniversalNavLinkType } from '../../../constants';
import { emitUserInteractionEvent } from '../../../utils/avroEvents';
import { useUniversalNavContext } from '../../../hooks';

type Props = {
  /**
   * Array of links to render within dropdown menu.
   */
  links: Array<UniversalNavLinkType>;
};

const Styles = labelStyles('Submenu', {
  root: css`
    background-color: ${Colors.neutral100};
    border: 1px solid ${Colors.neutral400};
    border-radius: 3px;
    padding: 20px;
    box-shadow: ${Shadows.elevation20};
  `,

  list: css`
    display: grid;
    grid-template-columns: 100%;
    gap: 5px;
  `,

  link: css`
    ${Typography.base};
    display: block;
    border-radius: 3px;
    color: ${Colors.neutral800};
    line-height: 25px;
    font-size: 18px;
    font-weight: 600;
    padding: 10px;
    text-decoration: none;

    &:hover,
    &:focus {
      color: ${Colors.neutral800};
      background-color: ${Colors.cream};
      outline: none;
    }
  `,

  linkInner: css`
    transform: translateY(1px);
  `,

  linkTitle: (hasDescription: boolean) => css`
    display: block;
    font-weight: ${hasDescription ? 600 : 500};
  `,

  linkDescription: css`
    display: block;
    margin-top: 5px;
    color: ${Colors.neutral800};
    font-size: 16px;
    line-height: 22px;
    font-weight: 400;
  `,
});

const SubmenuLink = (props: UniversalNavLinkType) => {
  const hasDescription = !!props.description;
  const { getFullyQualifiedURL } = useUniversalNavContext();

  const handleHover = () => {
    emitUserInteractionEvent({
      event_action: 'hover',
      option_selected: `UniversalLayout SubMenu: ${
        props.href ? props.href : props.text
      }`,
    });
  };

  const handleClick = () => {
    emitUserInteractionEvent({
      event_action: 'click',
      option_selected: `UniversalLayout SubMenu: ${
        props.href ? props.href : props.text
      }`,
    });
  };
  return (
    <Link
      color={null}
      css={Styles.link}
      href={getFullyQualifiedURL(props.href) || undefined}
      role="menuitem"
      onClick={() => handleClick()}
      onMouseEnter={() => handleHover()}
    >
      <div css={Styles.linkInner}>
        <p css={Styles.linkTitle(hasDescription)}>{props.text}</p>
        {props.description && (
          <p css={Styles.linkDescription}>{props.description}</p>
        )}
      </div>
    </Link>
  );
};

/**
 * @private
 * Submenu component acts as a dropdown on universal nav links
 */
const Submenu = (props: Props) => {
  const linkItems = props.links.map((link) => {
    // We set role="none" here in combination with role="menuitem" above on
    // the Link element for accessibility, given role="menu" in the ul below.
    // https://www.w3.org/TR/wai-aria-practices/examples/menu-button/menu-button-links.html
    return (
      <li key={link.text} role="none">
        <SubmenuLink {...link} />
      </li>
    );
  });
  return (
    <div css={Styles.root}>
      <ul css={Styles.list} role="menu">
        {linkItems}
      </ul>
    </div>
  );
};

export default Submenu;
